import { DownOutlined, EllipsisOutlined, UpOutlined } from '@ant-design/icons';
import {
  Dropdown, Space, Switch, Table, Typography,
} from 'antd';
import { orderBy } from 'lodash-es';
import React, {
  useContext, useEffect, useMemo, useState,
} from 'react';
import { captureException } from 'utils/errors';
import DraggableTable from 'components/DraggableTable';
import { MenuContext } from '../context';
import AddCategory, { CategoryInput } from './AddCategory';
import ItemsTable from './ItemsTable';
import {
  changeCategoryAvailability,
  deleteCategory,
  editCategory,
  reorderCategoryPosition,
} from './requests';
import './style.css';
import { getFilteredItems } from './utils';
import { POSHUB_MENU_DRIVER } from '../../constants';

function MenuItemsTable() {
  const {
    itemsLoading,
    menuDetail: menu,
    setItemsLoading,
    fetchMenuDetail,
    showMessage,
    searchTerm,
    selectedMenu,
    posHubIntegrated,
  } = useContext(MenuContext);
  const [categories, setCategories] = useState([]);
  const [editingCategory, setEditingCategory] = useState();

  const posHubReadOnly = posHubIntegrated;
  const posHubMenuAvailabilityLocked = menu?.migration_driver === POSHUB_MENU_DRIVER;

  const onRemoveCategory = (e, id) => {
    e.preventDefault();
    setItemsLoading(true);
    deleteCategory(id).then(() => {
      fetchMenuDetail();
    })
      .catch((e) => {
        captureException(e);
        showMessage({ type: 'error' });
        setItemsLoading(false);
      });
  };

  const onChangeCategoryAvailability = (id, currentValue) => {
    setItemsLoading(true);
    changeCategoryAvailability(id, !currentValue).then(() => {
      fetchMenuDetail();
    }).catch((e) => {
      captureException(e);
      showMessage({ type: 'error' });
      setItemsLoading(false);
    });
  };

  const nameColumn = {
    title: 'Name',
    key: 'name',
    dataIndex: 'name',
    inputType: 'text',
    editable: !posHubReadOnly,
    render: (value, data) => (
      <Space wrap>
        <Typography.Text>{value}</Typography.Text>
        {!!data.items?.length && (
          <Typography.Text style={{ fontSize: 12, color: '#666B71' }}>
            {data.items?.length}
            {' '}
            items
          </Typography.Text>
        )}
      </Space>
    ),
  };

  const availabilityColumn = {
    title: 'Availability',
    key: 'is_active',
    width: 120,
    align: 'center',
    render: (value, data) => (
      <Switch
        checked={data.is_active}
        onChange={() => onChangeCategoryAvailability(data.id, data.is_active)}
        disabled={itemsLoading || posHubReadOnly || posHubMenuAvailabilityLocked}
      />
    ),
  };

  const actionColumn = {
    key: 'action',
    width: 40,
    render: (value, data) => (data.key === editingCategory?.key ? null : (
      <Dropdown menu={{
        items: [
          {
            key: '1',
            label: <span role="presentation" onClick={() => setEditingCategory(data)}>Edit</span>,
          },
          {
            key: '2',
            label: <span role="presentation" onClick={(e) => onRemoveCategory(e, data.id)}>Remove</span>,
          },
        ],
      }}
      >
        <EllipsisOutlined />
      </Dropdown>
    )),
  };

  const sortColumn = {
    key: 'sort',
    width: 40,
    align: 'center',
  };

  const columns = posHubReadOnly
    ? [nameColumn, availabilityColumn, Table.EXPAND_COLUMN]
    : [sortColumn, nameColumn, availabilityColumn, actionColumn, Table.EXPAND_COLUMN];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType: 'text',
        dataIndex: col.dataIndex,
        title: col.title,
        editing: record.key === editingCategory?.key,
      }),
    };
  });

  const sortedCategories = useMemo(() => {
    const indexedCategories = menu.categories?.map((category) => ({ ...category, key: category.id })) || [];
    return orderBy(indexedCategories, ['position'], ['asc']);
  }, [menu]);
  useEffect(() => {
    if (menu.categories) {
      setCategories(sortedCategories);
    }
  }, [menu]);

  useEffect(() => {
    setCategories(getFilteredItems({
      searchTerm,
      categories: sortedCategories,
      hideEmptyCategories: posHubReadOnly,
    }));
  }, [searchTerm, sortedCategories, posHubReadOnly]);

  const onReorderCategory = ({ id, position }) => {
    setItemsLoading(true);
    reorderCategoryPosition({ categoryId: id, position }).then(() => {
    }).catch((e) => {
      captureException(e);
      showMessage({ type: 'error' });
    }).finally(() => setItemsLoading(false));
  };

  function customExpandIcon(props) {
    if (props.expanded) {
      return <UpOutlined onClick={(e) => props.onExpand(props.record, e)} />;
    }
    return <DownOutlined onClick={(e) => props.onExpand(props.record, e)} />;
  }

  const onEditCategory = (name) => {
    setItemsLoading(true);
    editCategory({ id: editingCategory?.id, name }).then(() => {
      fetchMenuDetail();
      setEditingCategory();
    }).catch((e) => {
      captureException(e);
      showMessage({ type: 'error' });
      setItemsLoading(false);
    });
  };

  const expandableConfig = {
    expandedRowRender: (record) => (
      <ItemsTable
        category={record}
        categories={categories}
        setCategories={setCategories}
        availabilityLocked={posHubMenuAvailabilityLocked}
      />
    ),
    expandIcon: customExpandIcon,
  };

  if (posHubReadOnly && !!categories?.length) {
    return (
      <div style={{ marginTop: 16 }}>
        <Table
          rowKey="key"
          columns={mergedColumns}
          dataSource={categories}
          loading={itemsLoading}
          pagination={false}
          showHeader={false}
          bordered
          expandable={expandableConfig}
          scroll={{ x: 400 }}
        />
      </div>
    );
  }

  if (!posHubReadOnly && !!categories?.length) {
    return (
      <div style={{ marginTop: 16 }}>
        <DraggableTable
          columns={mergedColumns}
          dataSource={categories}
          setDataSource={setCategories}
          showHeader={false}
          bordered
          loading={itemsLoading}
          onDragCallback={onReorderCategory}
          expandable={expandableConfig}
          CustomCell={(props) => (
            <EditableCell
              {...props}
              onCancel={() => setEditingCategory()}
              onSave={onEditCategory}
            />
          )}
          footer={posHubReadOnly ? undefined : () => <AddCategory buttonType="text" />}
          scroll={{ x: 400 }}
        />
      </div>
    );
  }

  return (
    <div style={{ marginTop: 16 }}>
      {selectedMenu && !categories?.length && !itemsLoading && posHubReadOnly && (
        <Typography.Text type="secondary">
          {searchTerm
            ? 'No items match your search in this POS menu.'
            : 'This POS menu currently has no visible categories/items in menu management.'}
        </Typography.Text>
      )}
      {selectedMenu && !categories?.length && !itemsLoading && !posHubReadOnly && <AddCategory />}
    </div>
  );
}

function EditableCell({
  editing,
  inputType,
  record,
  children,
  onCancel,
  onSave,
  ...restProps
}) {
  let inner = children;
  if (editing && inputType === 'text') {
    inner = <CategoryInput value={record.name} onCancel={onCancel} onSave={onSave} />;
  } else if (editing && inputType !== 'text') {
    inner = null;
  }
  return (
    <td {...restProps}>
      {inner}
    </td>
  );
}

export default MenuItemsTable;
